// src/pages/ManagerDailyLogViewPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, writeBatch, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/TeamPerformancePage.module.css'; // Reusing styles is efficient

// Helper to get a date string in YYYY-MM-DD format
const getDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const ManagerDailyLogViewPage = () => {
    const { currentUser } = useAuth();
    const [logs, setLogs] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [selectedDate, setSelectedDate] = useState(getDateString(new Date()));
    const [selectedUser, setSelectedUser] = useState('all');
    const [userTypeFilter, setUserTypeFilter] = useState('all'); // NEW: State for 'all', 'employee', 'trainee'

    const [ratings, setRatings] = useState({});
    const [notes, setNotes] = useState({});
    const [processingId, setProcessingId] = useState(null);

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch all users for the filter dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            if (!isMounted.current) return;
            try {
                const usersCol = collection(db, 'users');
                const usersSnapshot = await getDocs(query(usersCol, orderBy('name', 'asc')));
                if (!isMounted.current) return;
                const usersList = usersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || doc.data().email,
                    role: doc.data().role || 'Unknown' // Make sure role is fetched
                }));
                setAllUsers(usersList);
            } catch (err) {
                console.error("Error fetching users:", err);
                if (isMounted.current) setError("Failed to load user list.");
            }
        };
        fetchUsers();
    }, []);

    // Fetch logs based on the selected date
    useEffect(() => {
        if (!selectedDate) return;
        const fetchLogs = async () => {
            if (!isMounted.current) return;
            setLoading(true);
            setError('');
            try {
                const logsQuery = query(collection(db, 'dailyLogs'), where('logDate', '==', selectedDate));
                const querySnapshot = await getDocs(logsQuery);
                if (!isMounted.current) return;

                const logsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setLogs(logsData);

                const initialRatings = {};
                const initialNotes = {};
                logsData.forEach(log => {
                    if (log.rating) initialRatings[log.id] = log.rating;
                    if (log.managerNotes) initialNotes[log.id] = log.managerNotes;
                });
                setRatings(initialRatings);
                setNotes(initialNotes);

            } catch (err) {
                console.error(`Error fetching logs for ${selectedDate}:`, err);
                if (isMounted.current) setError("Failed to fetch logs for the selected date.");
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };
        fetchLogs();
    }, [selectedDate]);

    const handleRatingChange = (logId, rating) => {
        setRatings(prev => ({ ...prev, [logId]: rating }));
    };

    const handleNoteChange = (logId, text) => {
        setNotes(prev => ({ ...prev, [logId]: text }));
    };

    const handleSaveRating = async (log) => {
        if (!currentUser) return;
        const { id: logId, userId: logUserId, logDate } = log;
        if (isMounted.current) setProcessingId(logId);

        const rating = ratings[logId];
        const managerNotes = notes[logId] || '';

        if (!rating) {
            if (isMounted.current) {
                setError("Please select a rating before saving.");
                setProcessingId(null);
            }
            return;
        }

        const logRef = doc(db, 'dailyLogs', logId);
        const notificationRef = doc(collection(db, 'notifications'));
        const batch = writeBatch(db);

        const managerName = currentUser.displayName || currentUser.email;

        try {
            batch.update(logRef, {
                rating: Number(rating),
                managerNotes: managerNotes.trim(),
                ratedByUid: currentUser.uid,
                ratedByName: managerName,
                ratedAt: serverTimestamp(),
            });

            const notificationMessage = `${managerName} rated your daily log for ${logDate}.`;
            batch.set(notificationRef, {
                userId: logUserId,
                message: notificationMessage,
                link: `/daily-log#${logId}`,
                timestamp: serverTimestamp(),
                isRead: false,
                createdBy: currentUser.uid,
                createdByName: managerName,
                type: 'log_rated'
            });

            await batch.commit();

        } catch (err) {
            console.error("Error saving rating and sending notification:", err);
            if(isMounted.current) setError("Failed to save rating. Please try again.");
        } finally {
            if(isMounted.current) setProcessingId(null);
        }
    };
    
    // MODIFIED: Memoized lists for the dropdown based on user type filter
    const usersForFilterDropdown = useMemo(() => {
        if (userTypeFilter === 'employee') {
            return allUsers.filter(u => u.role?.toLowerCase() !== 'trainee');
        }
        if (userTypeFilter === 'trainee') {
            return allUsers.filter(u => u.role?.toLowerCase() === 'trainee');
        }
        return allUsers; // 'all' case
    }, [allUsers, userTypeFilter]);
    

    const filteredLogs = useMemo(() => {
        // First, filter by user type based on the log's 'userRole' field
        const typeFilteredLogs = logs.filter(log => {
            const role = log.userRole?.toLowerCase();
            if (userTypeFilter === 'employee') return role !== 'trainee';
            if (userTypeFilter === 'trainee') return role === 'trainee';
            return true; // 'all' case
        });

        // Then, filter by the selected individual user
        if (selectedUser === 'all') {
            return typeFilteredLogs;
        }
        return typeFilteredLogs.filter(log => log.userId === selectedUser);
    }, [logs, selectedUser, userTypeFilter]);
    
    const todayForDateInput = useMemo(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }, []);
    
    // Reset individual user filter when changing user type filter
    useEffect(() => {
        setSelectedUser('all');
    }, [userTypeFilter]);


    if (loading && allUsers.length === 0) {
        return <div className={styles.loadingIndicator}>Loading Performance Data...</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.pageTitle}>Review Daily Logs</h2>
            <div className={styles.filtersContainer}>
                <div className={styles.filterGroup}>
                    <label htmlFor="log-date-filter" className={styles.dateFilterLabel}>Date:</label>
                    <input
                        type="date"
                        id="log-date-filter"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={styles.dateInput}
                        max={todayForDateInput}
                    />
                </div>

                {/* MODIFIED: User type filter buttons */}
                <div className={styles.filterGroup}>
                    <label className={styles.dateFilterLabel}>View:</label>
                    <div className={styles.buttonGroup}>
                        <button 
                            className={userTypeFilter === 'all' ? styles.activeFilterButton : styles.filterButton}
                            onClick={() => setUserTypeFilter('all')}>All Staff</button>
                        <button 
                            className={userTypeFilter === 'employee' ? styles.activeFilterButton : styles.filterButton}
                            onClick={() => setUserTypeFilter('employee')}>Employees</button>
                        <button 
                            className={userTypeFilter === 'trainee' ? styles.activeFilterButton : styles.filterButton}
                            onClick={() => setUserTypeFilter('trainee')}>Trainees</button>
                    </div>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="user-filter" className={styles.dateFilterLabel}>Specific Person:</label>
                    <select
                        id="user-filter"
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="all">
                            {userTypeFilter === 'all' && 'All Staff'}
                            {userTypeFilter === 'employee' && 'All Employees'}
                            {userTypeFilter === 'trainee' && 'All Trainees'}
                        </option>
                        {usersForFilterDropdown.map(user => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.employeeStatsGrid}>
                {loading && <div className={styles.loadingIndicator}>Loading logs...</div>}
                {!loading && filteredLogs.length === 0 && (
                    <p className={styles.noDataMessage}>No work logs submitted for this day based on the current filters.</p>
                )}
                {filteredLogs.map(log => (
                    <div key={log.id} className={styles.employeeCard}>
                        <h3 className={styles.employeeName}>
                            {log.userName}
                            <span className={styles.employeeRole}>({log.userRole || 'Employee'})</span>
                        </h3>
                        <div className={styles.logContent}>
                            <h4>Work Performed:</h4>
                            <p>{log.logText}</p>
                        </div>
                        <div className={styles.ratingSection}>
                            <div className={styles.formGroup}>
                                <label htmlFor={`rating-${log.id}`}>Your Rating:</label>
                                <select 
                                    id={`rating-${log.id}`} 
                                    className={styles.filterSelectSmall}
                                    value={ratings[log.id] || ''}
                                    onChange={(e) => handleRatingChange(log.id, e.target.value)}
                                    disabled={processingId === log.id}
                                >
                                    <option value="">-- Rate --</option>
                                    <option value="5">5 - Excellent</option>
                                    <option value="4">4 - Good</option>
                                    <option value="3">3 - Satisfactory</option>
                                    <option value="2">2 - Needs Improvement</option>
                                    <option value="1">1 - Unsatisfactory</option>
                                </select>
                            </div>
                            <div className={styles.formGroup}>
                                <label htmlFor={`notes-${log.id}`}>Notes (Optional):</label>
                                <textarea
                                    id={`notes-${log.id}`}
                                    rows="2"
                                    placeholder="Add feedback for this entry..."
                                    value={notes[log.id] || ''}
                                    onChange={(e) => handleNoteChange(log.id, e.target.value)}
                                    disabled={processingId === log.id}
                                />
                            </div>
                            <button
                                onClick={() => handleSaveRating(log)}
                                className={styles.saveButtonSmall}
                                disabled={processingId === log.id || !ratings[log.id]}
                            >
                                {processingId === log.id ? 'Saving...' : 'Save Rating'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ManagerDailyLogViewPage;